/* STORY-003 — set the new password.
 *
 * Reached only from the emailed link. The token is in the URL and never shown,
 * never put in a form field, and never logged.
 */

import { PASSWORD_MIN_LENGTH, resetPasswordRequestSchema } from '@eventuley/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { AuthScreen, Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useResetPassword } from '../api/index.js';

/* The token comes from the URL, not from the person, so the form asks for the
 * password alone. Derived from the contract's schema so the length rule cannot
 * drift from the server's. */
const formSchema = resetPasswordRequestSchema.omit({ token: true });
type FormValues = z.infer<typeof formSchema>;

export function ResetPasswordScreen() {
  const t = useTranslate();
  const { token } = useSearch({ from: '/reset-password' });
  const reset = useResetPassword();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: { password: '' },
  });

  const onSubmit = handleSubmit((values) => {
    if (!token) return;
    reset.mutate({ token, password: values.password });
  });

  /* Only a DEAD LINK sends the person away. Everything else keeps the form.
   *
   * This screen used to collapse on `reset.isError`, which meant a dropped
   * connection or a 500 threw away a token that was still perfectly valid, and
   * offered "ask for a new link" as the only way out — TanStack does not retry
   * mutations, so nothing recovered on its own. The distinction the copy
   * already made was never made by the control flow.
   *
   * `token_invalid` covers expired, used, and never-existed: the server refuses
   * to say which, and there is genuinely nothing to retry. */
  const deadLink = reset.error instanceof ApiError && reset.error.code === 'token_invalid';

  /* No token at all — somebody typed the path, or a mail client mangled the
   * link. Same screen, because it is the same situation from the person's side
   * and the same action fixes it. */
  if (!token || deadLink) {
    return (
      <AuthScreen title={t('auth.reset.bad_link_title')} brand={t('app.name')}>
        <Callout tone="danger">{t('auth.reset.bad_link_body')}</Callout>
        <div className="auth__actions">
          <Link className="btn btn--secondary btn--block" to="/forgot-password">
            {t('auth.reset.request_another')}
          </Link>
        </div>
      </AuthScreen>
    );
  }

  if (reset.isSuccess) {
    return (
      <AuthScreen title={t('auth.reset.done_title')} brand={t('app.name')}>
        {/* Says out loud that the other sessions are gone. A silent security
            action is one nobody trusts happened — and somebody resetting
            because they suspect a compromise is asking exactly this question. */}
        <Callout tone="success">{t('auth.reset.done_body')}</Callout>
        <div className="auth__actions">
          <Link className="btn btn--primary btn--block" to="/sign-in">
            {t('auth.sign_in.submit')}
          </Link>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen title={t('auth.reset.title')} brand={t('app.name')}>
      {/* A failure that is ours, not the link's. The form stays below it with
          the typed password intact, so the person retries rather than going
          back for a second link they do not need. */}
      {reset.isError ? (
        <Callout tone="danger">
          {reset.error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      ) : null}

      <form onSubmit={onSubmit} noValidate>
        <TextField
          label={t('auth.reset.password_label')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.sign_up.password_hint')}
          minLength={PASSWORD_MIN_LENGTH}
          error={errors.password ? t('error.password_too_short') : undefined}
          {...register('password')}
        />

        <Button type="submit" block busy={isSubmitting || reset.isPending}>
          {isSubmitting || reset.isPending ? t('common.submitting') : t('auth.reset.submit')}
        </Button>
      </form>
    </AuthScreen>
  );
}
